import { getAdminApiUrl } from "./config";
import { forceRefreshTokens, loadTokens, refreshIfNeeded, type TokenBundle } from "./auth";

function bearerCandidates(t: TokenBundle): string[] {
  const out: string[] = [];
  const i = t.id_token?.trim();
  const a = t.access_token?.trim();
  if (i) out.push(i);
  if (a && a !== i) out.push(a);
  return out;
}

export type MeResponse = {
  sub: string;
  email: string;
  isSuperAdmin: boolean;
  bootstrapAvailable: boolean;
};

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const ref = await refreshIfNeeded();
  if (ref) throw new Error(ref);
  const t = loadTokens();
  const bearers = t ? bearerCandidates(t) : [];
  if (bearers.length === 0) throw new Error("Non autenticato.");

  const base = getAdminApiUrl();
  if (!base) throw new Error("VITE_ADMIN_API_URL mancante: esegui bash cloud/scripts/currents-web-sync-env.sh");

  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;

  const hdrs: Record<string, string> = {};
  if (init?.body && typeof init.body === "string") {
    hdrs["Content-Type"] = "application/json";
  }

  let lastErr = "Non autenticato.";
  for (let i = 0; i < bearers.length; i += 1) {
    const bearer = bearers[i]!;
    const res = await fetch(url, {
      ...init,
      headers: {
        ...hdrs,
        Authorization: `Bearer ${bearer}`,
        ...(init?.headers as Record<string, string> | undefined),
      },
    });

    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = { raw: text };
    }

    if (res.status === 401 && i < bearers.length - 1) {
      lastErr = "Sessione scaduta: esci e rientra.";
      continue;
    }

    if (res.status === 401) {
      throw new Error("Sessione scaduta: esci e rientra.");
    }

    if (!res.ok) {
      const msg =
        typeof data === "object" && data && "error" in data
          ? String((data as { error?: string }).error)
          : text.slice(0, 200);
      throw new Error(msg || `HTTP ${res.status}`);
    }

    return data as T;
  }

  throw new Error(lastErr);
}

export async function getMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/me");
}

export async function resolveUsers(subs: string[]): Promise<
  Array<{ sub: string; displayName?: string | null; email?: string | null }>
> {
  if (subs.length === 0) return [];
  const q = encodeURIComponent(subs.join(","));
  const data = await apiFetch<{ users: Array<{ sub: string; displayName?: string | null; email?: string | null }> }>(
    `/api/users/resolve?subs=${q}`,
  );
  return data.users ?? [];
}

export async function checkSuperAdminAccess(): Promise<MeResponse> {
  let me = await getMe();
  if (!me.isSuperAdmin && !me.bootstrapAvailable) {
    const refErr = await forceRefreshTokens();
    if (!refErr) {
      me = await getMe();
    }
  }
  return me;
}
